"use client";

import { useQuery } from "@tanstack/react-query";
import { useSession } from "next-auth/react";
import { type Control, type FieldValues, type Path } from "react-hook-form";
import { SelectField } from "./select-field";
import { MultiSelectField } from "./multi-select-field";
import { Skeleton } from "@/components/ui/skeleton";

interface DynamicSelectFieldProps<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  label: string;
  placeholder?: string;
  description?: string;
  disabled?: boolean;
  /** POST endpoint that returns a list of options */
  endpoint: string;
  /** Field in each result item to use as the option value. Default: "_id" */
  valueKey?: string;
  /** Field in each result item to use as the option label. Default: "title" */
  labelKey?: string;
  /** Optional body to POST to the endpoint */
  body?: Record<string, unknown>;
  /** Whether this is a multi-select field */
  isMulti?: boolean;
}

function getBaseUrl(): string {
  return (
    process.env.NEXT_PUBLIC_API_CLOUDFRONT ||
    process.env.NEXT_PUBLIC_BACKEND_URL ||
    "https://api.divineinfinitebeing.com/"
  );
}

export function DynamicSelectField<T extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  description,
  disabled = false,
  endpoint,
  valueKey = "_id",
  labelKey = "title",
  body = {
    condition: { status: 1, limit: 200, skip: 0 },
    searchcondition: {},
    sort: { type: "asc", field: "name" },
    project: {},
    token: "",
  },
  isMulti = false,
}: DynamicSelectFieldProps<T>) {
  const { data: session } = useSession();

  const { data, isLoading } = useQuery({
    queryKey: ["dynamic-select", endpoint, body],
    queryFn: async () => {
      const res = await fetch(`${getBaseUrl()}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(session?.token ? { Authorization: `Bearer ${session.token}` } : {}),
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`Failed to load options for ${label}`);
      const json = await res.json();
      // Handle various response structures: {data: []}, {results: {res: []}}, {results: []}, {dt: {res: []}}, {res: []}
      const result = json.data ?? json.results?.res ?? json.results ?? json.dt?.res ?? json.res ?? json;
      return (Array.isArray(result) ? result : []) as Record<string, unknown>[];
    },
    enabled: !!session?.token,
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-10 w-full rounded-md" />
      </div>
    );
  }

  const options = (Array.isArray(data) ? data : []).map((item) => ({
    value: String(item[valueKey] ?? ""),
    label: String(item[labelKey] ?? item["name"] ?? item["title"] ?? item["spread_name"] ?? item[valueKey] ?? ""),
  }));

  if (isMulti) {
    return (
      <MultiSelectField
        control={control}
        name={name}
        label={label}
        placeholder={placeholder}
        description={description}
        disabled={disabled}
        options={options}
      />
    );
  }

  return (
    <SelectField
      control={control}
      name={name}
      label={label}
      placeholder={placeholder}
      description={description}
      disabled={disabled}
      options={options}
    />
  );
}
